import { Link, useParams, Navigate } from "react-router-dom";
import { useState } from "react";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import CatalogueDialog from "@/components/CatalogueDialog";
import { getCategory, categories } from "@/data/categories";
import { getCluster } from "@/data/clusters";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { HomeDecorFamilies } from "@/components/HomeDecorFamilies";

export const CategoryDetail = () => {
  const { id } = useParams();
  const data = getCategory(id);
  const [catOpen, setCatOpen] = useState(false);

  useSeo({
    title: data
      ? `${data.name} sourcing from India`
      : "Category",
    description: data
      ? `${data.name} from India — ${data.tagline}. ${data.spec}.`.slice(0, 152)
      : undefined,
    path: data ? `/categories/${data.id}` : "/categories",
    image: data ? `/assets/og/${data.id}.jpg` : undefined,
    type: "article",
    jsonLd: data
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": `${SITE_URL}/categories/${data.id}#service`,
              name: `${data.name} sourcing from India`,
              description: data.intro,
              provider: { "@id": `${SITE_URL}#organization` },
              areaServed: { "@type": "Country", name: "India" },
              category: data.name,
              image: data.image,
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: `Why source ${data.name.toLowerCase()} from India?`,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: data.whyIndia.join(" "),
                  },
                },
                {
                  "@type": "Question",
                  name: `What testing does Asia Sourcing run on ${data.name.toLowerCase()}?`,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: data.testing
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(" "),
                  },
                },
                {
                  "@type": "Question",
                  name: `What is the MOQ and lead time for ${data.name.toLowerCase()}?`,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: data.spec,
                  },
                },
              ],
            },
            breadcrumb([
              { name: "Home", path: "/" },
              { name: "Categories", path: "/categories" },
              { name: data.name, path: `/categories/${data.id}` },
            ]),
          ],
        }
      : null,
    jsonLdId: "category-detail-jsonld",
  });

  if (!data) return <Navigate to="/" replace />;

  const idx = categories.findIndex((c) => c.id === id);
  const prev = categories[(idx - 1 + categories.length) % categories.length];
  const next = categories[(idx + 1) % categories.length];

  return (
    <article data-testid={`category-detail-${data.id}`}>
      {/* HERO */}
      <header
        style={{
          position: "relative",
          minHeight: "72svh",
          background: "var(--ink)",
          color: "var(--paper)",
          display: "flex",
          alignItems: "flex-end",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url("${data.id === "home-decor" ? "/assets/home-decor-hub-hero.jpg" : data.image}")`,
            backgroundSize: "cover",
            backgroundPosition: "center 42%",
            filter: "grayscale(.08) brightness(.5) contrast(1.15)",
            transform: "scale(1.06)",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(26,23,20,.3) 0%, rgba(26,23,20,.55) 55%, rgba(26,23,20,.97) 100%), linear-gradient(90deg, rgba(26,23,20,.88) 0%, rgba(26,23,20,.32) 60%, rgba(26,23,20,0) 100%)",
          }}
        />
        <div
          className="wrap"
          style={{
            position: "relative",
            zIndex: 2,
            paddingBlock: "calc(74px + var(--section)) clamp(3rem, 7vw, 6rem)",
            width: "100%",
          }}
        >
          <Reveal>
            <p
              className="eyebrow eyebrow-light"
              data-testid="category-eyebrow"
            >
              Category · {data.name} sourcing · India
            </p>
          </Reveal>
          <WordReveal
            as="h1"
            text={`${data.name}.`}
            className="display"
            stagger={70}
            testId="category-headline"
          />
          <Reveal as="p" delay={150} style={{ maxWidth: "60ch", color: "rgba(239,234,224,.78)", marginTop: "1.4rem", fontSize: "1.15rem", fontFamily: "var(--display)", fontStyle: "italic", fontWeight: 340 }}>
            {data.tagline}
          </Reveal>
          <Reveal as="p" delay={250} className="mono" style={{ color: "rgba(239,234,224,.55)", marginTop: "1rem" }}>
            {data.spec}
          </Reveal>
          <Reveal delay={350} style={{ marginTop: "2rem", display: "flex", flexWrap: "wrap", gap: ".8rem" }}>
            <button
              type="button"
              onClick={() => setCatOpen(true)}
              className="btn btn-fill btn-lg btn-glow"
              data-testid="catalogue-cta-hero"
            >
              <span>Download {data.name.toLowerCase()} catalogue (PDF) →</span>
            </button>
            <Link
              to={`/contact?category=${encodeURIComponent(data.name)}`}
              className="btn btn-ghost"
              style={{ borderColor: "rgba(239,234,224,.3)", color: "rgba(239,234,224,.85)" }}
              data-testid="category-hero-enquiry"
            >
              Send an enquiry
            </Link>
          </Reveal>
        </div>
      </header>

      {/* FACTS */}
      <section
        className="wrap"
        style={{
          paddingBlock: "clamp(2.5rem, 5vw, 4rem)",
          borderBottom: "1px solid var(--line)",
        }}
        data-testid="category-facts"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "1.6rem 2rem",
          }}
        >
          {data.facts.map(([k, v]) => (
            <Reveal key={k}>
              <span className="mono" style={{ color: "var(--brass-deep)" }}>{k}</span>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.2rem",
                  margin: ".3rem 0 0",
                  letterSpacing: "-.005em",
                }}
              >
                {v}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Sprint 28 · Home décor hub — 26 categories grouped by family (additive) */}
      {data.id === "home-decor" && <HomeDecorFamilies />}

      {/* INTRO */}
      <section className="wrap" style={{ paddingBlock: "var(--section)" }}>
        <div className="cap-grid">
          <Reveal>
            <p className="eyebrow">The category</p>
            <h2
              className="display"
              style={{ fontSize: "var(--t-h1)", margin: "1rem 0 1.4rem", lineHeight: 1.05 }}
            >
              {data.tagline}
            </h2>
            <p style={{ color: "var(--ink-soft)", maxWidth: "56ch", fontSize: "1.05rem" }}>
              {data.intro}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", marginTop: "1.4rem" }}>
              {data.materials.map((m) => (
                <span
                  key={m}
                  className="mono"
                  style={{
                    border: "1px solid var(--line)",
                    padding: ".45rem .8rem",
                    fontSize: ".68rem",
                    color: "var(--brass-deep)",
                    borderRadius: 2,
                  }}
                >
                  {m}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal>
            <p className="eyebrow">Finishes available</p>
            <ul className="cap-list" data-testid="category-finishes">
              {data.finishes.map((f) => (
                <li key={f}><span>{f}</span></li>
              ))}
            </ul>
            <p className="eyebrow" style={{ marginTop: "2rem" }}>Products developed</p>
            <ul className="cap-list" data-testid="category-products">
              {data.products.map((p) => (
                <li key={p}><span>{p}</span></li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* WHY INDIA */}
      <section
        className="wrap"
        style={{
          paddingBlock: "var(--section)",
          borderTop: "1px solid var(--line)",
          background: "var(--paper-raised)",
        }}
        data-testid="category-why-india"
      >
        <Reveal as="p" className="eyebrow">Why India</Reveal>
        <WordReveal
          as="h2"
          text={`Why ${data.name.toLowerCase()} from India — and not anywhere else.`}
          stagger={50}
          className="display"
        />
        <ol
          style={{
            listStyle: "none",
            counterReset: "why",
            padding: 0,
            marginTop: "2.4rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1.5rem",
          }}
        >
          {data.whyIndia.map((reason, i) => (
            <Reveal
              as="li"
              key={i}
              className="specimen"
              style={{ background: "var(--paper)" }}
              data-testid={`why-india-${i}`}
            >
              <span className="ix">/ {String(i + 1).padStart(2, "0")}</span>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.05rem",
                  lineHeight: 1.5,
                  margin: ".6rem 0 0",
                  color: "var(--ink)",
                  letterSpacing: "-.005em",
                }}
              >
                {reason}
              </p>
            </Reveal>
          ))}
        </ol>
      </section>

      {/* DIFFERENCE FROM REST OF WORLD */}
      <section
        className="wrap"
        style={{
          paddingBlock: "var(--section)",
          borderTop: "1px solid var(--line)",
        }}
        data-testid="category-difference"
      >
        <Reveal as="p" className="eyebrow">India vs the rest of the world</Reveal>
        <WordReveal
          as="h2"
          text="What India does that others can't."
          stagger={60}
          className="display"
        />
        <Reveal as="p" style={{ color: "var(--ink-soft)", maxWidth: "62ch", marginTop: "1.2rem" }} delay={120}>
          A straight comparison — what we hear from buyers who&apos;ve sourced from each geography.
        </Reveal>
        <div
          style={{
            marginTop: "2.4rem",
            border: "1px solid var(--line)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {data.difference.map(([geo, take], i) => (
            <Reveal
              key={geo}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(140px, 220px) 1fr",
                gap: "1.5rem",
                padding: "1.4rem 1.6rem",
                borderBottom: i < data.difference.length - 1 ? "1px solid var(--line)" : "none",
                background: geo.includes("India") ? "var(--paper-raised)" : "transparent",
              }}
            >
              <span
                className="mono"
                style={{
                  color: geo.includes("India") ? "var(--brass-deep)" : "var(--ink-soft)",
                  fontSize: ".82rem",
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  fontWeight: geo.includes("India") ? 600 : 400,
                }}
              >
                {geo}
              </span>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.05rem",
                  lineHeight: 1.5,
                  margin: 0,
                  color: "var(--ink)",
                  letterSpacing: "-.005em",
                }}
              >
                {take}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTING & INSPECTION */}
      <section
        className="testing"
        style={{ borderTop: "1px solid var(--line)" }}
        data-testid="category-testing"
      >
        <div className="wrap">
          <div className="head">
            <Reveal as="p" className="eyebrow">Testing &amp; inspection — {data.name}</Reveal>
            <Reveal as="h2">Every test, every certificate, photographed and archived.</Reveal>
            <Reveal as="p" className="lede" delay={120}>
              The protocols Asia Sourcing runs on every {data.name.toLowerCase()} container — in-house and
              through ISO-accredited third-party labs.
            </Reveal>
          </div>
          <div className="testing-grid">
            {data.testing.map(([k, v], i) => (
              <Reveal
                key={k}
                className="test-tile"
                data-testid={`category-test-${i}`}
              >
                <span className="ix">/ {String(i + 1).padStart(2, "0")}</span>
                <h3>{k}</h3>
                <p>{v}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLUSTERS THAT MAKE IT */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid="category-clusters"
      >
        <Reveal as="p" className="eyebrow">Clusters that make it</Reveal>
        <WordReveal
          as="h2"
          text={`Where ${data.name.toLowerCase()} is actually made.`}
          stagger={55}
          className="display"
        />
        <div
          style={{
            marginTop: "2.4rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1.4rem",
          }}
        >
          {data.clusters
            .map((cid) => getCluster(cid))
            .filter(Boolean)
            .map((c) => (
              <Reveal key={c.id}>
                <Link
                  to={`/clusters/${c.id}`}
                  className="product-card"
                  data-testid={`category-cluster-${c.id}`}
                  style={{ display: "block" }}
                >
                  <img src={c.image} alt={`${c.name} cluster — ${c.material}`} width="800" height="600" loading="lazy" />
                  <div className="label">
                    <span className="cat">{c.name} · {c.state}</span>
                    <h3>{c.nickname}</h3>
                    <span className="arrow">SEE CLUSTER →</span>
                  </div>
                </Link>
              </Reveal>
            ))}
        </div>
      </section>

      {/* CTA */}
      <section
        className="cta wrap"
        style={{ borderTop: "1px solid var(--line)" }}
      >
        <Reveal as="p" className="eyebrow">Start an enquiry</Reveal>
        <Reveal as="h2" className="display">
          {data.cta}.
        </Reveal>
        <Reveal className="row">
          <Link
            to={`/contact?category=${encodeURIComponent(data.name)}`}
            className="btn btn-fill btn-lg btn-glow"
            data-testid="category-cta-enquiry"
          >
            <span>Send an enquiry →</span>
          </Link>
          <button
            type="button"
            onClick={() => setCatOpen(true)}
            className="btn btn-ghost"
            data-testid="catalogue-cta-bottom"
          >
            Download PDF catalogue
          </button>
          <Link to="/clusters" className="btn btn-ghost">See all clusters</Link>
        </Reveal>
      </section>

      {/* Sprint B · Global Buyers grid — only surfaces on the two SEO pillars (furniture + lighting) */}
      {(data.id === "furniture" || data.id === "lighting") && (
        <section
          className="wrap"
          data-testid={`category-${data.id}-global-buyers`}
          style={{
            paddingBlock: "clamp(2.4rem, 5vw, 4rem)",
            borderTop: "1px solid var(--line)",
          }}
        >
          <Reveal as="p" className="eyebrow" style={{ marginBottom: ".8rem" }}>
            {data.name} sourcing for global buyers
          </Reveal>
          <Reveal
            as="h2"
            className="display"
            style={{
              fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
              margin: "0 0 1rem",
              letterSpacing: "-.01em",
            }}
          >
            {data.name} sourcing from India — by destination market
          </Reveal>
          <Reveal
            as="p"
            style={{
              color: "var(--ink-soft)",
              fontSize: "1.02rem",
              lineHeight: 1.7,
              maxWidth: "72ch",
              margin: "0 0 1.6rem",
            }}
          >
            Country-specific {data.name.toLowerCase()} sourcing playbooks — Indian manufacturing
            clusters mapped to destination-market compliance stacks, port lanes and buyer profiles.
            Each page carries the same operational discipline: supplier verification, factory audit,
            sample development, production monitoring and pre-shipment inspection.
          </Reveal>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "1rem",
            }}
          >
            {[
              { label: "United Kingdom", ports: "Felixstowe · Southampton", slug: `/regions/uk/${data.id === "furniture" ? "furniture-sourcing" : "lighting-sourcing"}` },
              { label: "Germany", ports: "Hamburg · Bremerhaven", slug: `/regions/germany/${data.id === "furniture" ? "furniture-sourcing" : "lighting-sourcing"}` },
              { label: "France", ports: "Le Havre · Marseille-Fos", slug: `/regions/france/${data.id === "furniture" ? "furniture-sourcing" : "lighting-sourcing"}` },
              { label: "Netherlands", ports: "Rotterdam", slug: `/regions/netherlands/${data.id === "furniture" ? "furniture-sourcing" : "lighting-sourcing"}` },
              { label: "Sweden", ports: "Gothenburg · via Rotterdam", slug: `/regions/sweden/${data.id === "furniture" ? "furniture-sourcing" : "lighting-sourcing"}` },
              { label: "Finland", ports: "HaminaKotka · Helsinki", slug: `/regions/finland/${data.id === "furniture" ? "furniture-sourcing" : "lighting-sourcing"}` },
              { label: "USA", ports: "Long Beach · New York", slug: "/usa" },
              { label: "Nordics — sustainability hub", ports: "Rotterdam feeder + direct", slug: "/regions/nordic-sustainable-sourcing" },
            ].map((country, i) => (
              <Link
                key={i}
                to={country.slug}
                data-testid={`category-${data.id}-country-${country.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                style={{
                  display: "block",
                  padding: "1.3rem 1.4rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                  transition: "border-color 200ms, transform 200ms",
                }}
              >
                <p
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".62rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: "0 0 .5rem",
                  }}
                >
                  {data.name} · {country.label}
                </p>
                <p
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: "1.08rem",
                    letterSpacing: "-.005em",
                    margin: "0 0 .6rem",
                    lineHeight: 1.35,
                  }}
                >
                  {data.name} sourcing from India to {country.label} →
                </p>
                <p
                  style={{
                    color: "var(--ink-soft)",
                    fontSize: ".88rem",
                    lineHeight: 1.55,
                    margin: 0,
                  }}
                >
                  Ports: {country.ports}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* PAGINATION */}
      <nav
        className="wrap"
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          paddingBlock: "2rem 4rem",
          borderTop: "1px solid var(--line)",
          fontFamily: "var(--mono)",
          fontSize: ".74rem",
          letterSpacing: ".08em",
          textTransform: "uppercase",
        }}
        aria-label="Category pagination"
        data-testid="category-pagination"
      >
        <Link to={`/categories/${prev.id}`} className="btn btn-ghost" data-testid="category-prev">
          ← {prev.name}
        </Link>
        <Link to={`/categories/${next.id}`} className="btn btn-ghost" data-testid="category-next">
          {next.name} →
        </Link>
      </nav>

      <CatalogueDialog open={catOpen} onOpenChange={setCatOpen} category={data} />
    </article>
  );
};

export default CategoryDetail;
